// Wire format for Dialpad's Mini Dialer (CTI) iframe, from
// https://developers.dialpad.com/docs/dialpad-mini-dialer. Every message, in
// either direction, is this envelope posted with window.postMessage.
export const DIALPAD_CTI_API = "opencti_dialpad";
export const DIALPAD_CTI_VERSION = "1.0";
export const DIALPAD_CTI_PATH = "/apps/open";

// Dialpad asks for these attributes verbatim; without `allow` the browser never
// offers the iframe the microphone.
export const DIALPAD_CTI_ALLOW =
  "microphone; speaker-selection; autoplay; camera; display-capture; hid";
export const DIALPAD_CTI_SANDBOX =
  "allow-popups allow-scripts allow-same-origin allow-forms";

export type OutboundMethod = "hang_up_all_calls" | "initiate_call";

export interface InitiateCallPayload {
  phone_number: string;
  custom_data?: string;
}

export interface CtiEnvelope {
  api: typeof DIALPAD_CTI_API;
  version: typeof DIALPAD_CTI_VERSION;
  method: OutboundMethod;
  payload?: InitiateCallPayload;
}

export type InboundMessage =
  | {
      method: "user_authentication";
      userAuthenticated: boolean;
      userId: number | null;
    }
  | { method: "login_required" }
  | {
      method: "call_ringing";
      callId: string | null;
      // "on" when the call starts ringing, "off" when it stops.
      ringing: boolean;
    };

// A trailing slash or path in a configured origin would never equal
// MessageEvent#origin, so compare and post against the bare origin only.
export function normalizeOrigin(origin: string): string {
  return new URL(origin).origin;
}

export function ctiUrl(origin: string): string {
  return `${normalizeOrigin(origin)}${DIALPAD_CTI_PATH}`;
}

export function buildMessage(
  method: OutboundMethod,
  payload?: InitiateCallPayload
): CtiEnvelope {
  const envelope: CtiEnvelope = {
    api: DIALPAD_CTI_API,
    version: DIALPAD_CTI_VERSION,
    method
  };
  return payload ? { ...envelope, payload } : envelope;
}

// initiate_call takes E.164 only. Sera stores numbers as typed, e.g.
// "(444) 444-4444", and its customers are North American, so a bare ten-digit
// number gets +1. Anything that cannot be read as a full number is refused
// rather than dialled half-right.
export function toE164(phoneNumber: string | null | undefined): string | null {
  if (!phoneNumber) return null;

  const trimmed = phoneNumber.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (trimmed.startsWith("+")) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

type Payload = Record<string, unknown>;

const isRecord = (value: unknown): value is Payload =>
  typeof value === "object" && value !== null;

function parseUserAuthentication(payload: Payload): InboundMessage | null {
  if (typeof payload.user_authenticated !== "boolean") return null;

  const userId = payload.user_id;
  return {
    method: "user_authentication",
    userAuthenticated: payload.user_authenticated,
    userId:
      payload.user_authenticated && typeof userId === "number" ? userId : null
  };
}

// Dialpad's id for an incoming call, as a string the way Sera stores it.
function parseCallRinging(payload: Payload): InboundMessage {
  const { id } = payload;
  const callId =
    (typeof id === "number" && Number.isInteger(id)) ||
    (typeof id === "string" && /^\d+$/.test(id))
      ? String(id)
      : null;
  return { method: "call_ringing", callId, ringing: payload.state === "on" };
}

// Dialpad posts with target origin "*", so any window can send this page a
// look-alike message. Only trust one that comes from the embedded iframe's own
// window, on the Dialpad origin, in the CTI envelope. Anything else, including
// a Dialpad login popup on the same origin, is ignored. Only the fields the
// page needs are kept; the rest of the payload (e.g. an id_token) is dropped.
export function parseInboundMessage(
  event: MessageEvent,
  expectedOrigin: string,
  expectedSource: MessageEventSource | null
): InboundMessage | null {
  if (!expectedSource || event.source !== expectedSource) return null;
  if (event.origin !== normalizeOrigin(expectedOrigin)) return null;
  if (!isRecord(event.data) || event.data.api !== DIALPAD_CTI_API) return null;

  const payload = isRecord(event.data.payload) ? event.data.payload : {};
  switch (event.data.method) {
    case "user_authentication":
      return parseUserAuthentication(payload);
    // Undocumented, but the CTI emits it when it shows its log-in button.
    case "login_required":
      return { method: "login_required" };
    // Sent when an incoming call starts and stops ringing.
    case "call_ringing":
      return parseCallRinging(payload);
    default:
      return null;
  }
}
